"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ArrowRight, PhoneCall, ShieldCheck, Wrench, Flame, Droplets, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface BrandItem {
  name: string;
  category: string;
  specialty: string;
  tag: string;
}
const BRANDS_LIST: BrandItem[] = [{
  name: "Bradford White",
  category: "Water Heaters",
  specialty: "Tank & Tankless Gas/Electric",
  tag: "Certified Partner"
}, {
  name: "Rheem",
  category: "Water Heaters & Boilers",
  specialty: "High-Efficiency Hybrid Systems",
  tag: "Factory Trained"
}, {
  name: "Moen",
  category: "Faucets & Valves",
  specialty: "Flo Smart Valves & Cartridges",
  tag: "OEM Parts"
}, {
  name: "Kohler",
  category: "Fixtures & Sinks",
  specialty: "Pressure-Lite Toilets & Faucets",
  tag: "Authorized Service"
}, {
  name: "Delta",
  category: "Fixtures & Drains",
  specialty: "Diamond Seal Technology",
  tag: "Full Inventory"
}, {
  name: "Rinnai",
  category: "Tankless Water Heaters",
  specialty: "Commercial & Residential Units",
  tag: "Master Techs"
}, {
  name: "A.O. Smith",
  category: "Water Heaters",
  specialty: "Signature High-Recovery Systems",
  tag: "Emergency Stock"
}, {
  name: "American Standard",
  category: "Toilets & Basins",
  specialty: "Champion Flushing Systems",
  tag: "Genuine Replacement"
}, {
  name: "InSinkErator",
  category: "Waste Disposers",
  specialty: "Evolution & Pro Series Units",
  tag: "Same-Day Installs"
}, {
  name: "Navien",
  category: "Condensing Tankless",
  specialty: "NPE Series Gas Systems",
  tag: "Rapid Diagnostics"
}, {
  name: "Grohe",
  category: "Luxury Fixtures",
  specialty: "Thermostatic Shower Mixers",
  tag: "Precision Repair"
}, {
  name: "Zoeller",
  category: "Sump & Sewage Pumps",
  specialty: "Mighty-Mate Submersibles",
  tag: "Flood Defense"
}];
const TRUST_POINTS = ["Direct OEM replacement parts on all trucks", "Factory warranty compliance guaranteed", "Zero markup on emergency brand diagnostics", "Immediate availability for Jonesboro homes"];
export default function Brands() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.05,
        duration: 0.3
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="brands-serviced" data-nav="light" className="bg-background py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-12">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-bold uppercase tracking-wider mb-4">
              <ShieldCheck className="w-4 h-4 text-primary" />
              <span>Authorized Service &amp; OEM Standards</span>
            </div>
            <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight leading-tight">
              Plumbing &amp; Water Heater Brands We Service Daily
            </h2>
            <p className="mt-4 text-base md:text-lg text-muted-foreground font-sans leading-relaxed">
              Our Jonesboro emergency technicians carry genuine factory parts for all major residential and commercial manufacturers to resolve repairs in a single trip.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild className="rounded-full bg-accent text-accent-foreground font-bold hover:brightness-105 shadow-md px-6 h-11">
              <a href="tel:(470) 847-9076" className="inline-flex items-center gap-2">
                <PhoneCall className="w-4 h-4" />
                <span>(470) 847-9076</span>
              </a>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-border bg-card text-card-foreground font-semibold hover:bg-muted px-6 h-11">
              <Link to="/brands/" className="inline-flex items-center gap-2">
                <span>View All Brands</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Brand Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {BRANDS_LIST.map((brand, i) => <motion.div key={brand.name} data-index={i} variants={itemVariants}>
              <Card className="h-full border border-border bg-card rounded-xl p-0 gap-0 shadow-xs hover:border-primary/40 hover:bg-background transition-[background-color,border-color,box-shadow] duration-200 group">
                <CardContent className="p-5 flex flex-col justify-between h-full">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold">
                        <Sparkles className="w-3 h-3 text-primary" />
                        {brand.tag}
                      </span>
                      <span className="text-xs text-muted-foreground font-mono">
                        24/7 Parts
                      </span>
                    </div>

                    <h3 className="font-default text-xl font-bold text-foreground tracking-tight group-hover:text-primary transition-colors">
                      {brand.name}
                    </h3>
                    <p className="text-sm font-medium text-muted-foreground mt-0.5">
                      {brand.category}
                    </p>
                  </div>

                  <div className="mt-5 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                    <span className="truncate pr-2 font-sans">{brand.specialty}</span>
                    <Wrench className="w-4 h-4 text-primary shrink-0 opacity-80 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom Trust & Diagnostic Strip */}
        <div className="mt-12 rounded-2xl bg-secondary/50 border border-border p-6 md:p-8 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Droplets className="w-5 h-5 text-primary" />
              <h4 className="font-default text-lg font-bold text-foreground">
                Don't See Your Specific Brand Listed?
              </h4>
            </div>
            <p className="text-sm text-muted-foreground max-w-2xl font-sans">
              We stock universal fittings, heavy-duty commercial adapters, and factory-compatible valves for vintage and specialty systems throughout Jonesboro and Clayton County.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
              {TRUST_POINTS.map((point, i) => <div key={point} data-index={i} className="flex items-center gap-2 text-xs font-medium text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                  <span>{point}</span>
                </div>)}
            </div>
          </div>

          <div className="shrink-0 flex flex-col sm:flex-row lg:flex-col gap-3">
            <Button asChild className="rounded-full bg-primary text-primary-foreground font-semibold px-6 h-11 hover:bg-primary/90 shadow-sm">
              <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                <span>Check Equipment Compatibility</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-border bg-background text-foreground font-medium px-6 h-11 hover:bg-card">
              <Link to="/water-heater/" className="inline-flex items-center justify-center gap-2">
                <Flame className="w-4 h-4 text-accent" />
                <span>Water Heater Diagnostics</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}